import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Divider,
  Autocomplete,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ptBR from 'date-fns/locale/pt-BR';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import commercialVisitService from '../services/commercialVisit.service';
import clientService, { Client } from '../services/client.service';
import userService from '../services/user.service';
import checklistTemplateService, { ChecklistTemplate } from '../services/checklistTemplate.service';
import { 
  CommercialVisitFormValues, 
  COMMERCIAL_VISIT_TYPE_LABELS, 
  COMMERCIAL_VISIT_STATUS_LABELS,
  COMMERCIAL_VISIT_PRIORITY_LABELS
} from '../types/commercialVisit.types';
import AuthLayout from '../components/AuthLayout';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { UserType } from '../types/user.types';

const CommercialVisitForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(id ? true : false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  // Estados para referências
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [representatives, setRepresentatives] = useState<UserType[]>([]);
  const [checklistTemplates, setChecklistTemplates] = useState<ChecklistTemplate[]>([]);
  const [loadingClients, setLoadingClients] = useState<boolean>(false);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [loadingRepresentatives, setLoadingRepresentatives] = useState<boolean>(false);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);
  
  // Esquema de validação
  const validationSchema = Yup.object({
    client: Yup.string().required('Cliente é obrigatório'),
    type: Yup.string().required('Tipo de visita é obrigatório'),
    description: Yup.string().required('Descrição é obrigatória'),
    status: Yup.string().required('Status é obrigatório'),
    priority: Yup.string().required('Prioridade é obrigatória'),
    assignedTo: Yup.string().required('Responsável é obrigatório'),
    scheduledDate: Yup.date().required('Data agendada é obrigatória'),
    notes: Yup.string()
  });
  
  // Valores iniciais do formulário
  const initialValues: CommercialVisitFormValues = {
    client: '',
    type: 'presentation',
    description: '',
    status: 'scheduled',
    priority: 'medium',
    assignedTo: '',
    representative: '',
    scheduledDate: new Date().toISOString(),
    location: {
      address: '',
      latitude: undefined,
      longitude: undefined
    },
    checklistTemplate: '',
    notes: ''
  };
  
  // Formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('CommercialVisitForm: Iniciando submit do formulário');
        console.log('CommercialVisitForm: Valores do formulário:', JSON.stringify(values, null, 2));
        
        if (id) {
          // Atualizar visita existente
          console.log('CommercialVisitForm: Atualizando visita existente:', id);
          await commercialVisitService.updateCommercialVisit(id, values);
          setSuccess(true);
          showSnackbar('Visita comercial atualizada com sucesso!', 'success');
        } else {
          // Criar nova visita
          console.log('CommercialVisitForm: Criando nova visita');
          const result = await commercialVisitService.createCommercialVisit(values);
          console.log('CommercialVisitForm: Visita criada com sucesso:', result);
          setSuccess(true);
          showSnackbar('Visita comercial criada com sucesso!', 'success');
        }
        
        // Redirecionar após um breve delay para mostrar a mensagem de sucesso
        setTimeout(() => {
          navigate('/commercial-visits');
        }, 1500);
      } catch (error: any) {
        console.error('CommercialVisitForm: Erro ao salvar visita comercial:', error);
        console.error('CommercialVisitForm: Detalhes do erro:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setError(error.response?.data?.message || 'Erro ao salvar visita comercial. Tente novamente.');
        showSnackbar('Erro ao salvar visita comercial. Tente novamente.', 'error');
      } finally {
        setLoading(false);
      }
    }
  });
  
  // Carregar visita para edição
  const loadVisit = async () => {
    if (!id) return;
    
    try {
      setInitialLoading(true);
      const visit = await commercialVisitService.getCommercialVisitById(id);
      
      // Formatar data
      const formattedVisit = {
        ...visit,
        scheduledDate: visit.scheduledDate,
        client: visit.client._id || visit.client,
        assignedTo: visit.assignedTo._id || visit.assignedTo,
        representative: visit.representative?._id || visit.representative || '',
        checklistTemplate: visit.checklistTemplate?._id || visit.checklistTemplate || ''
      };
      
      formik.setValues(formattedVisit);
      
      // Carregar dados do cliente para preencher endereço e coordenadas
      const clientId = visit.client._id || visit.client;
      if (clientId) {
        try {
          console.log('CommercialVisitForm: Carregando dados do cliente na edição:', clientId);
          const response = await clientService.getClientById(clientId);
          const clientData = response.client || response;
          
          // Preencher endereço e coordenadas se não estiverem na visita
          if (clientData.address) {
            const fullAddress = `${clientData.address.street || ''}, ${clientData.address.number || ''} - ${clientData.address.city || ''}, ${clientData.address.state || ''}`.trim();
            
            // Só preenche se não houver endereço na visita
            if (!visit.location?.address) {
              formik.setFieldValue('location.address', fullAddress);
            }
            
            // Preencher coordenadas do cliente
            if (clientData.address.coordinates && clientData.address.coordinates.lat && clientData.address.coordinates.lng) {
              formik.setFieldValue('location.latitude', clientData.address.coordinates.lat);
              formik.setFieldValue('location.longitude', clientData.address.coordinates.lng);
            }
          }
        } catch (error: any) {
          console.error('CommercialVisitForm: Erro ao carregar dados do cliente na edição:', error);
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar visita comercial:', error);
      setError(error.response?.data?.message || 'Erro ao carregar visita comercial. Tente novamente.');
      showSnackbar('Erro ao carregar visita comercial. Tente novamente.', 'error');
    } finally {
      setInitialLoading(false);
    }
  };
  
  // Carregar clientes
  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await clientService.getClients({ limit: 1000, isActive: true });
      // Verificar o formato da resposta
      let clientsList: Client[] = [];
      if (response.clients) {
        clientsList = response.clients;
      } else if (Array.isArray(response)) {
        clientsList = response;
      } else {
        console.error('Formato de resposta inesperado do serviço de clientes:', response);
      }
      setClients(clientsList);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };
  
  // Carregar usuários
  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await userService.getUsers({ isActive: true });
      // Verificar o formato da resposta
      let usersList: UserType[] = [];
      if (response.users) {
        usersList = response.users;
      } else if (Array.isArray(response)) {
        usersList = response;
      } else {
        console.error('Formato de resposta inesperado do serviço de usuários:', response);
      }
      setUsers(usersList);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Carregar representantes comerciais
  const loadRepresentatives = async () => {
    try {
      setLoadingRepresentatives(true);
      console.log('CommercialVisitForm: Carregando representantes comerciais...');
      const response = await userService.getCommercialAgents();
      console.log('CommercialVisitForm: Resposta do serviço de representantes:', response);
      
      // Verificar o formato da resposta
      let representativesList: UserType[] = [];
      if (response && response.users) {
        representativesList = response.users;
        console.log('CommercialVisitForm: Representantes encontrados:', representativesList.length);
      } else if (Array.isArray(response)) {
        representativesList = response;
        console.log('CommercialVisitForm: Representantes (array direto):', representativesList.length);
      } else {
        console.warn('CommercialVisitForm: Formato de resposta inesperado:', response);
        representativesList = [];
      }
      setRepresentatives(representativesList);
    } catch (error: any) {
      console.error('CommercialVisitForm: Erro ao carregar representantes:', error);
      console.error('CommercialVisitForm: Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // Não propagar o erro, apenas definir lista vazia
      setRepresentatives([]);
    } finally {
      setLoadingRepresentatives(false);
    }
  };
  
  // Carregar templates de checklist
  const loadChecklistTemplates = async () => {
    try {
      setLoadingTemplates(true);
      console.log('CommercialVisitForm: Carregando templates de checklist...');
      const response = await checklistTemplateService.getChecklistTemplates({ isActive: true });
      console.log('CommercialVisitForm: Resposta do serviço de templates:', response);
      
      // Verificar o formato da resposta
      let templatesList: ChecklistTemplate[] = [];
      if (response && response.templates) {
        templatesList = response.templates;
        console.log('CommercialVisitForm: Templates encontrados:', templatesList.length);
      } else if (Array.isArray(response)) {
        templatesList = response;
        console.log('CommercialVisitForm: Templates (array direto):', templatesList.length);
      } else {
        console.warn('CommercialVisitForm: Formato de resposta inesperado:', response);
        templatesList = [];
      }
      setChecklistTemplates(templatesList);
    } catch (error: any) {
      console.error('CommercialVisitForm: Erro ao carregar templates:', error);
      console.error('CommercialVisitForm: Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // Não propagar o erro, apenas definir lista vazia
      setChecklistTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  // Carregar dados do cliente e preencher latitude/longitude
  const handleClientChange = async (clientId: string) => {
    formik.setFieldValue('client', clientId);
    
    if (clientId) {
      try {
        console.log('CommercialVisitForm: Carregando dados do cliente:', clientId);
        const response = await clientService.getClientById(clientId);
        const clientData = response.client || response;
        console.log('CommercialVisitForm: Dados do cliente:', clientData);
        
        // Preencher endereço e coordenadas
        if (clientData.address) {
          const fullAddress = `${clientData.address.street || ''}, ${clientData.address.number || ''} - ${clientData.address.city || ''}, ${clientData.address.state || ''}`.trim();
          formik.setFieldValue('location.address', fullAddress);
          
          if (clientData.address.coordinates && clientData.address.coordinates.lat && clientData.address.coordinates.lng) {
            formik.setFieldValue('location.latitude', clientData.address.coordinates.lat);
            formik.setFieldValue('location.longitude', clientData.address.coordinates.lng);
            console.log('CommercialVisitForm: Coordenadas preenchidas:', {
              lat: clientData.address.coordinates.lat,
              lng: clientData.address.coordinates.lng
            });
          } else {
            console.log('CommercialVisitForm: Cliente não possui coordenadas');
            formik.setFieldValue('location.latitude', undefined);
            formik.setFieldValue('location.longitude', undefined);
          }
        }
      } catch (error: any) {
        console.error('CommercialVisitForm: Erro ao carregar dados do cliente:', error);
      }
    } else {
      // Limpar campos se nenhum cliente for selecionado
      formik.setFieldValue('location.address', '');
      formik.setFieldValue('location.latitude', undefined);
      formik.setFieldValue('location.longitude', undefined);
    }
  };
  
  // Efeitos para carregar dados iniciais
  useEffect(() => {
    if (id) {
      loadVisit();
    }
    
    loadClients();
    loadUsers();
    loadRepresentatives();
    loadChecklistTemplates();
  }, [id]);
  
  return (
    <AuthLayout title="Formulário de Visita Comercial">
      <Box sx={{ p: 3 }}>
        {/* Breadcrumbs de navegação */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={() => navigate('/commercial-visits')}
            underline="hover"
            color="inherit"
          >
            Visitas Comerciais
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Editar Visita Comercial' : 'Nova Visita Comercial'}
          </Typography>
        </Breadcrumbs>

        {/* Título e botão de voltar */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/commercial-visits')}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Typography variant="h5">
            {isEditMode ? 'Editar Visita Comercial' : 'Nova Visita Comercial'}
          </Typography>
        </Box>

        {/* Mensagens de erro ou sucesso */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Visita comercial salva com sucesso!
          </Alert>
        )}

        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                {/* Cliente */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={formik.touched.client && Boolean(formik.errors.client)}>
                    <Autocomplete
                      id="client"
                      options={clients}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return `${option.name} (${option.documentNumber})`;
                      }}
                      value={clients.find(c => c._id === formik.values.client) || null}
                      onChange={(_, newValue) => {
                        handleClientChange(newValue?._id || '');
                      }}
                      loading={loadingClients}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cliente"
                          error={formik.touched.client && Boolean(formik.errors.client)}
                          helperText={formik.touched.client && formik.errors.client}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                
                {/* Tipo de Visita */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth error={formik.touched.type && Boolean(formik.errors.type)}>
                    <InputLabel>Tipo de Visita</InputLabel>
                    <Select
                      id="type"
                      name="type"
                      value={formik.values.type}
                      onChange={formik.handleChange}
                      label="Tipo de Visita"
                    >
                      {Object.entries(COMMERCIAL_VISIT_TYPE_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.type && formik.errors.type && (
                      <FormHelperText>{formik.errors.type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                {/* Status */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth error={formik.touched.status && Boolean(formik.errors.status)}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      id="status"
                      name="status"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                      label="Status"
                    >
                      {Object.entries(COMMERCIAL_VISIT_STATUS_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.status && formik.errors.status && (
                      <FormHelperText>{formik.errors.status}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                {/* Prioridade */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth error={formik.touched.priority && Boolean(formik.errors.priority)}>
                    <InputLabel>Prioridade</InputLabel>
                    <Select
                      id="priority"
                      name="priority"
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                      label="Prioridade"
                    >
                      {Object.entries(COMMERCIAL_VISIT_PRIORITY_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.priority && formik.errors.priority && (
                      <FormHelperText>{formik.errors.priority}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                {/* Responsável */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={formik.touched.assignedTo && Boolean(formik.errors.assignedTo)}>
                    <Autocomplete
                      id="assignedTo"
                      options={users}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.displayName || option.email;
                      }}
                      value={users.find(u => u._id === formik.values.assignedTo) || null}
                      onChange={(_, newValue) => {
                        formik.setFieldValue('assignedTo', newValue ? newValue._id : '');
                      }}
                      loading={loadingUsers}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Responsável"
                          error={formik.touched.assignedTo && Boolean(formik.errors.assignedTo)}
                          helperText={formik.touched.assignedTo && formik.errors.assignedTo}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                
                {/* Representante */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      id="representative"
                      options={representatives}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.displayName || option.email;
                      }}
                      value={representatives.find(r => r._id === formik.values.representative) || null}
                      onChange={(_, newValue) => {
                        formik.setFieldValue('representative', newValue ? newValue._id : '');
                      }}
                      loading={loadingRepresentatives}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Representante Comercial"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingRepresentatives ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                
                {/* Template de Checklist */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Autocomplete
                      id="checklistTemplate"
                      options={checklistTemplates}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.name;
                      }}
                      value={checklistTemplates.find(t => t._id === formik.values.checklistTemplate) || null}
                      onChange={(_, newValue) => {
                        formik.setFieldValue('checklistTemplate', newValue ? newValue._id : '');
                      }}
                      loading={loadingTemplates}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Template de Checklist"
                          helperText="Selecione um template de checklist para ser preenchido durante a visita"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingTemplates ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                
                {/* Descrição */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    label="Descrição"
                    multiline
                    rows={2}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                  />
                </Grid>
                
                {/* Data Agendada */}
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Agendada"
                      value={formik.values.scheduledDate ? new Date(formik.values.scheduledDate) : null}
                      onChange={(date) => formik.setFieldValue('scheduledDate', date ? date.toISOString() : null)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          error: formik.touched.scheduledDate && Boolean(formik.errors.scheduledDate),
                          helperText: formik.touched.scheduledDate && formik.errors.scheduledDate
                        } 
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                
                {/* Valor da Oportunidade */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="opportunityValue"
                    name="opportunityValue"
                    label="Valor da Oportunidade (R$)"
                    type="number"
                    value={formik.values.opportunityValue || ''}
                    onChange={formik.handleChange}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                </Grid>
                
                {/* Endereço */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="location.address"
                    name="location.address"
                    label="Endereço"
                    value={formik.values.location?.address || ''}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Preenchido automaticamente ao selecionar o cliente"
                    disabled={!formik.values.location?.address}
                  />
                </Grid>
                
                {/* Latitude */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="location.latitude"
                    name="location.latitude"
                    label="Latitude"
                    value={formik.values.location?.latitude || ''}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Preenchido automaticamente ao selecionar o cliente"
                    disabled={!formik.values.location?.latitude}
                  />
                </Grid>
                
                {/* Longitude */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="location.longitude"
                    name="location.longitude"
                    label="Longitude"
                    value={formik.values.location?.longitude || ''}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Preenchido automaticamente ao selecionar o cliente"
                    disabled={!formik.values.location?.longitude}
                  />
                </Grid>
                
                {/* Observações */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    label="Observações"
                    multiline
                    rows={4}
                    value={formik.values.notes || ''}
                    onChange={formik.handleChange}
                  />
                </Grid>
                
                {/* Botões */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate('/commercial-visits')}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          Salvando...
                        </>
                      ) : (
                        'Salvar'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Box>
    </AuthLayout>
  );
};

export default CommercialVisitForm;
